import React, { useState } from 'react';
import { TextField, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TopNavbar from '../../components/TopNavBar/TopNavBar';
import CountrySelect from '../../components/CountriesDropBox/CountriesDropBox';
import {
    BackgroundContainer,
    LoginContainer,
    PaperContainer,
    LogoContainer,
    LoginHeader,
    FormContainer,
    SubmitButton,
    BackButton,
} from './RegisterPage.styles';
import { signup, checkEmailAvailability } from '../../api/authApi';
import {useToast} from '../../components/ToastProvider/ToastProvider';

function RegisterPage() {
    const [step, setStep] = useState(1);
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [mobile, setMobile] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [mobileError, setMobileError] = useState('');
    const [errors, setErrors] = useState({});

    const [billing, setBilling] = useState({
        street: '',
        suburb: '',
        city: '',
        state: '',
        postal: '',
        country: '',
    });
    const [shipping, setShipping] = useState({
        street: '',
        suburb: '',
        city: '',
        state: '',
        postal: '',
        country: '',
    });
    const [accountType, setAccountType] = useState('individual');
    const [businessInfo, setBusinessInfo] = useState({
        legalName: '',
        registrationNumber: '',
        vatNumber: '',
        eoriNumber: '',
    });
    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();
    const { showToast } = useToast();

    const handlePasswordChange = (setter) => (e) => {
        const newPassword = e.target.value;
        setter(newPassword);
        if (setter === setPassword) {
            if (newPassword.length > 0 && newPassword.length < 8) {
                setPasswordError('Password must be at least 8 characters long');
            } else {
                setPasswordError('');
            }
        }
        setErrors((prev) => ({ ...prev, password: false }));
    };

    const isPasswordValid = password.length >= 8;

    // Mobile number validation
    const validateMobileNumber = (mobileNumber) => {
        const cleanMobile = mobileNumber.replace(/\D/g, ''); // Remove non-digits
        return cleanMobile.length >= 4 && cleanMobile.length <= 20;
    };

    const handleMobileChange = (e) => {
        const mobileValue = e.target.value;
        setMobile(mobileValue);
        
        if (mobileValue && !validateMobileNumber(mobileValue)) {
            setMobileError('Mobile number must be at least 4 digits');
        } else {
            setMobileError('');
        }
        
        setErrors((prev) => ({ ...prev, mobile: false }));
    };

    const validateStep1 = () => {
        const newErrors = {};
        if (!fullName.trim()) newErrors.fullName = true;
        if (!email.trim()) newErrors.email = true;
        if (!mobile.trim()) newErrors.mobile = true;
        if (!validateMobileNumber(mobile)) newErrors.mobile = true;
        if (!isPasswordValid) newErrors.password = true;
        if (!confirmPassword.trim()) newErrors.confirmPassword = true;
        if (password !== confirmPassword) newErrors.confirmPassword = true;

        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            showToast('Please fill in all required fields correctly', 'error');
            return false;
        }
        return true;
    };

    const [checkingEmail, setCheckingEmail] = useState(false);

    const handleNext = async () => {
        if (!validateStep1()) return;
        setCheckingEmail(true);
        try {
            await checkEmailAvailability(email.trim());
            setStep(2);
        } catch (err) {
            const msg = err.message || 'Email check failed';
            showToast(msg, 'error');
            setErrors((prev) => ({ ...prev, email: true }));
        } finally {
            setCheckingEmail(false);
        }
    };

    const handleBack = () => setStep(1);

    const handleSameAsAbove = () => {
        const allFilled = Object.values(billing).every(Boolean);
        if (allFilled) {
            setShipping({ ...billing });
            showToast('Shipping address copied from billing', 'success');
            clearError('shipping');
        } else {
            showToast('Please fill in all billing fields first', 'warning');
        }
    };

    const validateStep2 = () => {
        const newErrors = { billing: {}, shipping: {} };
        const requiredBilling = ['street', 'city', 'state', 'postal', 'country'];
        const requiredShipping = ['street', 'city', 'state', 'postal', 'country'];

        for (const field of requiredBilling) {
            if (!billing[field]?.trim()) newErrors.billing[field] = true;
        }
        for (const field of requiredShipping) {
            if (!shipping[field]?.trim()) newErrors.shipping[field] = true;
        }

        const hasErrors =
            Object.keys(newErrors.billing).length > 0 ||
            Object.keys(newErrors.shipping).length > 0;

        if (hasErrors) {
            setErrors(newErrors);
            showToast('Please fill in all required billing and shipping fields', 'error');
            return false;
        }
        return true;
    };

    const handleFinalNext = () => {
        if (validateStep2()) setStep(3);
    };

    const validateBusinessInfo = () => {
        if (accountType === 'individual') return true;

        const newErrors = { businessInfo: {} };
        if (!businessInfo.legalName.trim()) newErrors.businessInfo.legalName = true;

        const hasErrors = Object.keys(newErrors.businessInfo).length > 0;

        if (hasErrors) {
            setErrors(newErrors);
            showToast('Please fill in required business information fields', 'error');
            return false;
        }
        return true;
    };

    const handleComplete = async () => {
        if (!validateBusinessInfo()) return;

        setIsLoading(true);
        try {
            const shippingSameAsBilling =
                shipping.street === billing.street &&
                shipping.suburb === billing.suburb &&
                shipping.city === billing.city &&
                shipping.state === billing.state &&
                shipping.postal === billing.postal &&
                shipping.country === billing.country;

            const signupData = {
                customer_info: {
                    full_name: fullName.trim(),
                    email: email.trim(),
                    mobile_no: mobile.replace(/\D/g, ''),
                    password,
                    confirm_password: confirmPassword,
                },
                billing_shipping: {
                    shipping_same_as_billing: shippingSameAsBilling,
                    billing_address: {
                        street_address: billing.street,
                        suburb_locality: billing.suburb || null,
                        city_town: billing.city,
                        state_province_region: billing.state,
                        postal_zip_code: billing.postal,
                        country: billing.country,
                    },
                    shipping_address: shippingSameAsBilling
                        ? null
                        : {
                              street_address: shipping.street,
                              suburb_locality: shipping.suburb || null,
                              city_town: shipping.city,
                              state_province_region: shipping.state,
                              postal_zip_code: shipping.postal,
                              country: shipping.country,
                          },
                },
                business_info: {
                    is_individual: accountType === 'individual',
                    legal_business_name: accountType === 'individual' ? null : businessInfo.legalName,
                    registration_number:
                        accountType === 'individual'
                            ? null
                            : (businessInfo.registrationNumber?.trim() || null),
                    vat_tax_number: accountType === 'individual' ? null : (businessInfo.vatNumber?.trim() || null),
                    eori_import_export_number: accountType === 'individual' ? null : businessInfo.eoriNumber,
                },
                is_admin: false,
            };

            const data = await signup(signupData);

            if (data.access_token) localStorage.setItem('accessToken', data.access_token);
            localStorage.setItem('isAdmin', data.is_admin ? 'true' : 'false');

            showToast('Registration successful! Redirecting...', 'success');
            navigate('/login');
        } catch (err) {
            console.error('Signup error:', err);
            showToast(err.message || 'Signup failed', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const clearError = (fieldPath) => {
        setErrors((prev) => {
            const updated = { ...prev };
            const parts = fieldPath.split('.');
            if (parts.length === 1) delete updated[parts[0]];
            else if (parts.length === 2 && updated[parts[0]]) delete updated[parts[0]][parts[1]];
            return updated;
        });
    };

    return (
        <>
            <TopNavbar />
            <BackgroundContainer>
                <LoginContainer>
                    <PaperContainer elevation={3}>
                        <LogoContainer>
                            <img src="/APD_Login.png" alt="Logo" />
                        </LogoContainer>

                        <LoginHeader variant="h5" component="h1">
                            {step === 1
                                ? 'Customer Information'
                                : step === 2
                                ? 'Billing & Shipping'
                                : 'Business Information'}
                        </LoginHeader>

                        {/* STEP 1 */}
                        {step === 1 && (
                            <FormContainer>
                                <TextField
                                    required
                                    fullWidth
                                    label="Full Name"
                                    value={fullName}
                                    onChange={(e) => {
                                        setFullName(e.target.value);
                                        clearError('fullName');
                                    }}
                                    error={!!errors.fullName}
                                    helperText={errors.fullName ? 'This field is required' : ''}
                                />
                                <TextField
                                    required
                                    fullWidth
                                    label="Email Address"
                                    type="email"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        clearError('email');
                                    }}
                                    error={!!errors.email}
                                    helperText={errors.email ? 'This field is required' : ''}
                                />
                                <TextField
                                    required
                                    fullWidth
                                    label="Mobile No"
                                    type="tel"
                                    value={mobile}
                                    onChange={handleMobileChange}
                                    error={!!mobileError || !!errors.mobile}
                                    helperText={mobileError || (errors.mobile ? 'Mobile number is required and must be at least 4 digits' : '')}
                                />
                                <TextField
                                    required
                                    fullWidth
                                    label="Enter Password"
                                    type="password"
                                    value={password}
                                    onChange={handlePasswordChange(setPassword)}
                                    error={!!errors.password || !!passwordError}
                                    helperText={passwordError || (errors.password ? 'This field is required' : 'Minimum 8 characters required')}
                                />
                                <TextField
                                    required
                                    fullWidth
                                    label="Confirm Password"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value);
                                        clearError('confirmPassword');
                                    }}
                                    error={!!errors.confirmPassword}
                                    helperText={errors.confirmPassword ? 'Passwords must match' : ''}
                                />
                                <SubmitButton
                                    fullWidth
                                    variant="contained"
                                    onClick={handleNext}
                                    disabled={checkingEmail}
                                >
                                    {checkingEmail ? 'Checking...' : 'Next'}
                                </SubmitButton>
                            </FormContainer>
                        )}

                        {/* STEP 2 */}
                        {step === 2 && (
                            <FormContainer>
                                <h3>Billing Address</h3>
                                {['street', 'suburb', 'city', 'state', 'postal'].map((field) => (
                                    <TextField
                                        key={field}
                                        required
                                        fullWidth
                                        label={field.charAt(0).toUpperCase() + field.slice(1)}
                                        value={billing[field]}
                                        onChange={(e) => {
                                            setBilling({ ...billing, [field]: e.target.value });
                                            clearError(`billing.${field}`);
                                        }}
                                        error={!!errors.billing?.[field]}
                                        helperText={errors.billing?.[field] ? 'This field is required' : ''}
                                    />
                                ))}
                                <CountrySelect
                                    value={billing.country}
                                    onChange={(value) => {
                                        setBilling({ ...billing, country: value });
                                        clearError('billing.country');
                                    }}
                                />
                                {errors.billing?.country && (
                                    <Typography color="error" variant="caption" style={{ marginTop: '4px' }}>
                                        This field is required
                                    </Typography>
                                )}

                                <h3>Shipping Address</h3>
                                <BackButton type="button" onClick={handleSameAsAbove} style={{ marginBottom: '16px' }}>
                                    Same as above
                                </BackButton>
                                {['street', 'suburb', 'city', 'state', 'postal'].map((field) => (
                                    <TextField
                                        key={field}
                                        required
                                        fullWidth
                                        label={field.charAt(0).toUpperCase() + field.slice(1)}
                                        value={shipping[field]}
                                        onChange={(e) => {
                                            setShipping({ ...shipping, [field]: e.target.value });
                                            clearError(`shipping.${field}`);
                                        }}
                                        error={!!errors.shipping?.[field]}
                                        helperText={errors.shipping?.[field] ? 'This field is required' : ''}
                                    />
                                ))}
                                <CountrySelect
                                    value={shipping.country}
                                    onChange={(value) => {
                                        setShipping({ ...shipping, country: value });
                                        clearError('shipping.country');
                                    }}
                                />
                                {errors.shipping?.country && (
                                    <Typography color="error" variant="caption" style={{ marginTop: '4px' }}>
                                        This field is required
                                    </Typography>
                                )}

                                <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '20px' }}>
                                    <BackButton onClick={handleBack}>Back</BackButton>
                                    <SubmitButton type="button" onClick={handleFinalNext}>
                                        Next
                                    </SubmitButton>
                                </div>
                            </FormContainer>
                        )}

                        {/* STEP 3 */}
                        {step === 3 && (
                            <FormContainer>
                                <FormControl component="fieldset" style={{ marginBottom: '24px', width: '100%' }}>
                                    <FormLabel component="legend" style={{ 
                                        fontSize: '1.1rem', 
                                        fontWeight: 'bold', 
                                        color: '#002F87',
                                        marginBottom: '16px'
                                    }}>
                                        Select Type
                                    </FormLabel>
                                    <RadioGroup
                                        value={accountType}
                                        onChange={(e) => setAccountType(e.target.value)}
                                        style={{ 
                                            flexDirection: 'row', 
                                            justifyContent: 'center',
                                            gap: '32px'
                                        }}
                                    >
                                        <FormControlLabel 
                                            value="individual" 
                                            control={<Radio sx={{ 
                                                color: '#002F87',
                                                '&.Mui-checked': {
                                                    color: '#002F87',
                                                }
                                            }} />} 
                                            label={<Typography style={{ fontSize: '1rem', fontWeight: '500' }}>Individual</Typography>}
                                        />
                                        <FormControlLabel 
                                            value="business" 
                                            control={<Radio sx={{ 
                                                color: '#002F87',
                                                '&.Mui-checked': {
                                                    color: '#002F87',
                                                }
                                            }} />} 
                                            label={<Typography style={{ fontSize: '1rem', fontWeight: '500' }}>Business</Typography>}
                                        />
                                    </RadioGroup>
                                </FormControl>

                                {accountType === 'business' && (
                                    <>
                                        <Typography variant="h6" style={{ 
                                            marginBottom: '16px', 
                                            color: '#002F87',
                                            fontWeight: 'bold'
                                        }}>
                                            Business Information
                                        </Typography>
                                        <TextField
                                            required
                                            fullWidth
                                            label="Legal Business Name"
                                            value={businessInfo.legalName}
                                            onChange={(e) => {
                                                setBusinessInfo({ ...businessInfo, legalName: e.target.value });
                                                clearError('businessInfo.legalName');
                                            }}
                                            error={!!errors.businessInfo?.legalName}
                                            helperText={errors.businessInfo?.legalName ? 'This field is required' : ''}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Registration Number (optional)"
                                            value={businessInfo.registrationNumber}
                                            onChange={(e) =>
                                                setBusinessInfo({ ...businessInfo, registrationNumber: e.target.value })}
                                        />
                                        <TextField
                                            fullWidth
                                            label="VAT Number (optional)"
                                            value={businessInfo.vatNumber}
                                            onChange={(e) => setBusinessInfo({ ...businessInfo, vatNumber: e.target.value })}
                                        />
                                        <TextField
                                            fullWidth
                                            label="EORI / Import-Export Number (optional)"
                                            value={businessInfo.eoriNumber}
                                            onChange={(e) => setBusinessInfo({ ...businessInfo, eoriNumber: e.target.value })}
                                        />
                                    </>
                                )}

                                <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '20px' }}>
                                    <BackButton onClick={() => setStep(2)} disabled={isLoading}>Back</BackButton>
                                    <SubmitButton 
                                        type="button" 
                                        onClick={handleComplete}
                                        disabled={isLoading}
                                        sx={{
                                            backgroundColor: isLoading ? '#9e9e9e' : '#002F87',
                                            '&:hover': {
                                                backgroundColor: isLoading ? '#9e9e9e' : '#001f5c',
                                            },
                                            '&:disabled': {
                                                backgroundColor: '#9e9e9e',
                                                color: '#fff',
                                            }
                                        }}
                                    >
                                        {isLoading ? 'Loading...' : 'Complete'}
                                    </SubmitButton>
                                </div>
                            </FormContainer>
                        )}
                    </PaperContainer>
                </LoginContainer>
            </BackgroundContainer>
        </>
    );
}

export default RegisterPage;
